import * as React from 'react';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/initialize';

import type { Moment } from 'moment';
import moment from 'moment';

import user from 'js/lib/user';
import { MOMENT_FORMATS, formatDateTimeDisplay, momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import 'css!./__styles__/DateInput';
// eslint-disable-next-line no-restricted-syntax
import 'css!react-dates/lib/css/_datepicker.css';

import type { DateRangeValue } from './DateRangeInput';

type Props = {
  id: string;
  value?: string;
  disabled?: boolean;
  onChange: (value?: string) => void;
  selectRange?: 'start' | 'end';
  otherDate?: string;
  appendToBody?: boolean;
  limit?: DateRangeValue;
  forceUserTimeZone?: boolean;
  placeholder?: string;
};

type State = {
  isFocused: boolean;
};

/**
 * Notes:
 *
 * In the gradebook export drawer, we cannot appendToBody because the translateX
 * interferes with how the date picker figures out where it belongs. I could not
 * find a way to fix this. This is noticeable if the scroll position is not at
 * the top of the page because of the way react-dates works.
 */
export class DateInput extends React.Component<Props, State> {
  state = {
    isFocused: false,
  };

  handleOnDateChange = (date: Moment | null) => {
    const { onChange } = this.props;

    onChange(date ? formatDateTimeDisplay(date, MOMENT_FORMATS.YEAR_MONTH_DAY, null, 'en') : undefined);
  };

  handleOnFocusChange = ({ focused }: { focused: boolean | null }) => {
    this.setState({ isFocused: !!focused });
  };

  isOutsideRange = (day: Moment) => {
    const { selectRange, otherDate, limit } = this.props;
    const currentMoment = moment(day.format());

    if (selectRange) {
      if (selectRange === 'start') {
        const endLimit =
          otherDate && limit?.endDate
            ? moment.min(moment(otherDate), moment(limit.endDate))
            : otherDate || limit?.endDate;

        if (endLimit && limit?.startDate) {
          return currentMoment.isAfter(endLimit) || currentMoment.isBefore(limit?.startDate);
        } else if (endLimit) {
          return currentMoment.isAfter(endLimit);
        } else if (limit?.startDate) {
          return currentMoment.isBefore(limit.startDate);
        } else {
          return false;
        }
      }

      if (selectRange === 'end') {
        const startLimit =
          otherDate && limit?.startDate
            ? moment.max(moment(otherDate), moment(limit.startDate))
            : otherDate || limit?.startDate;

        if (startLimit && limit?.endDate) {
          return !currentMoment.isBetween(startLimit, limit?.endDate, undefined, '[]');
        } else if (startLimit) {
          return currentMoment.isBefore(startLimit);
        } else if (limit?.endDate) {
          return currentMoment.isAfter(limit.endDate);
        } else {
          return false;
        }
      }
    }

    return false;
  };

  render() {
    const { id, value, disabled, forceUserTimeZone, appendToBody = false, placeholder = 'm/d/yyyy' } = this.props;
    const { isFocused } = this.state;

    const date = value ? momentWithUserTimezone(value, forceUserTimeZone ? user.getUserTimezone() : 'UTC') : null;

    //
    // prop orders below are according to documentation of component.
    //
    return (
      <SingleDatePicker
        date={date}
        onDateChange={this.handleOnDateChange}
        focused={isFocused}
        onFocusChange={this.handleOnFocusChange}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        showClearDate
        showDefaultInputIcon
        inputIconPosition="after"
        openDirection="down"
        numberOfMonths={1}
        hideKeyboardShortcutsPanel
        isOutsideRange={this.isOutsideRange}
        displayFormat={MOMENT_FORMATS.US_LOCAL_DATE}
        appendToBody={appendToBody}
      />
    );
  }
}
